import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Zap } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import RevealLines from "./RevealLines";
import Reveal from "./Reveal";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/d63da504-59d9-46e2-b5e7-f0d7372ddd6f/images/baff874078f1b67ecabad336865d39b80db0420ab301b9df50adcf762176e5a9.png";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const badgeY = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);

  return (
    <section
      ref={ref}
      id="top"
      data-testid="hero-section"
      className="relative pt-28 lg:pt-36 pb-16 lg:pb-28 overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.035] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(#1A1B1A 1px, transparent 1px), linear-gradient(90deg, #1A1B1A 1px, transparent 1px)",
          backgroundSize: "72px 72px",
        }}
      />

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end relative">
        <div className="lg:col-span-7 relative">
          <motion.div
            initial={{ opacity: 0, x: -14 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.05, duration: 0.7 }}
            className="flex items-center gap-3 mb-8"
          >
            <span className="overline text-clay">SERVICII IT · EST. 2014</span>
            <span className="h-px w-16 bg-line" />
            <span className="overline text-inksoft">V.02 — 2026</span>
          </motion.div>

          <RevealLines
            className="font-display font-black text-5xl sm:text-6xl lg:text-[92px] tracking-[-0.035em]"
            lines={[
              <span key="l1">Soluții IT</span>,
              <span key="l2" className="italic font-light">de încredere</span>,
              <span key="l3">
                pentru <span className="text-clay">afacerea ta.</span>
              </span>,
            ]}
          />

          <Reveal delay={0.7} y={16} className="mt-8 max-w-xl">
            <p className="text-base lg:text-lg text-inksoft leading-relaxed">
              Echipă dedicată pentru afaceri și case. Reparăm, configurăm,
              monitorizăm — așa încât tu să te ocupi de business, nu de cabluri.
            </p>
          </Reveal>

          <Reveal delay={0.85} y={16} className="mt-10 flex flex-wrap items-center gap-4">
            <Link
              to="/contact"
              data-testid="hero-primary-cta"
              className="group inline-flex items-center gap-3 px-6 py-4 rounded-full bg-ink text-sand font-semibold text-sm hover:bg-clay hover:text-white transition-colors"
            >
              Cere o ofertă gratuită
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/servicii"
              data-testid="hero-secondary-cta"
              className="inline-flex items-center gap-2 px-6 py-4 rounded-full border border-ink/20 text-ink font-semibold text-sm hover:border-ink hover:bg-white transition-all"
            >
              Vezi serviciile
            </Link>
          </Reveal>

          <Reveal delay={1.0} y={16} className="mt-14 flex flex-wrap gap-10 text-sm">
            <Stat value="< 4h" label="timp mediu intervenție" />
            <Stat value="11 ani" label="experiență practică" />
            <Stat value="24/7" label="linie abonați" />
          </Reveal>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-5 relative"
        >
          <div className="relative rounded-2xl overflow-hidden border border-line shadow-[0_30px_60px_-40px_rgba(26,27,26,0.45)]">
            <motion.img
              src={HERO_IMG}
              alt="Infrastructură IT profesională"
              className="w-full h-[520px] object-cover"
              loading="eager"
              style={{ y: imgY, scale: imgScale }}
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-ink/40 via-transparent to-transparent" />
            <motion.div
              style={{ y: badgeY }}
              className="absolute top-5 left-5 flex items-center gap-2 px-3 py-2 rounded-full bg-sand/90 backdrop-blur-sm border border-line"
            >
              <Zap size={14} className="text-clay" />
              <span className="text-xs font-semibold">
                Disponibilitate 24/7 cu abonament
              </span>
            </motion.div>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 20, rotate: -3 }}
            animate={{ opacity: 1, y: 0, rotate: -3 }}
            transition={{ delay: 1.1, duration: 0.7 }}
            className="absolute -bottom-6 -left-6 bg-ink text-sand p-5 rounded-xl border border-ink shadow-xl max-w-[220px] hidden lg:block"
          >
            <span className="overline text-clay">ACOPERIRE</span>
            <p className="mt-2 font-display text-xl font-bold leading-tight">
              On-site & remote
            </p>
            <p className="mt-1 text-xs text-sand/70">
              Intervenție rapidă oriunde ești.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

function Stat({ value, label }) {
  return (
    <div className="flex flex-col">
      <span className="font-display text-3xl font-bold tracking-tight">{value}</span>
      <span className="text-[11px] text-inksoft uppercase tracking-[0.2em] mt-1">
        {label}
      </span>
    </div>
  );
}
